import React from 'react';
import { Link } from 'react-router-dom';
import { submitWaitlist } from '../services/waitlistService';
import './LandingPage.css';

const LandingPage: React.FC = () => {
  const [email, setEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!valid) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const utm = typeof window !== 'undefined' ? (window.location.search || '') : '';
      await submitWaitlist({
        email,
        consent: true,
        sourcePath: typeof window !== 'undefined' ? window.location.pathname : '/',
        utm,
        createdAt: new Date().toISOString(),
      });
      setSuccess("You're on the waitlist! We'll be in touch shortly.");
      setEmail('');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Something went wrong. Please try again later.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: 'bi-robot', title: 'AI-Powered Analysis', description: 'Advanced algorithms analyze your profile against thousands of successful applicants to identify your competitive advantages.' },
    { icon: 'bi-bullseye', title: 'Strategic Positioning', description: 'Develop a compelling narrative that differentiates you from similarly qualified applicants at elite institutions.' },
    { icon: 'bi-bar-chart', title: 'Data-Driven Matching', description: 'Get personalized college lists with realistic admission probability calculations for each institution.' },
    { icon: 'bi-people', title: 'Competitive Analysis', description: 'Understand how your profile stacks up against typical admitted students at your target schools.' },
    { icon: 'bi-lightbulb', title: 'STEM Excellence', description: 'Specialized guidance for STEM-focused applicants applying to top technical programs and research universities.' },
    { icon: 'bi-award', title: 'Elite Expertise', description: 'Insights from top-tier college counselors among the best in the United States, now powered by AI.' },
  ];

  const steps = [
    { number: '01', title: 'Share your profile', description: 'Tell us about your academics, extracurriculars, and college goals.' },
    { number: '02', title: 'Get insights', description: 'Receive detailed analysis of your competitive positioning and opportunities.' },
    { number: '03', title: 'Build your list', description: 'Access personalized college recommendations with admission probabilities.' },
    { number: '04', title: 'Refine your strategy', description: 'Use interactive tools to compare schools and optimize your approach.' },
  ];

  const highlightCards = [
    { icon: 'bi-bullseye', label: 'Strategic Planning', value: '100%' },
    { icon: 'bi-graph-up', label: 'Data Analysis', value: 'AI-Powered' },
    { icon: 'bi-people', label: 'Expert Guidance', value: '24/7' },
    { icon: 'bi-award', label: 'Success Rate', value: '94%' },
  ];

  return (
    <div className="new-landing-page">
      {/* Hero Section */}
      <section className="new-hero">
        <nav className="new-nav">
          <div className="new-container">
            <div className="new-nav-content">
              <span className="new-logo">CollegeIQ.ai</span>
              <div className="new-nav-actions">
                <Link to="/login" className="new-nav-link">Sign In</Link>
                <Link to="/signup" className="new-btn new-btn-dark">Get Started</Link>
              </div>
            </div>
          </div>
        </nav>

        <div className="new-container">
          <div className="new-hero-grid">
            <div className="new-hero-content">
              <h1 className="new-hero-title">
                Your path to
                <span className="new-hero-title-block">elite colleges</span>
              </h1>

              <p className="new-hero-subtitle">
                Data-driven guidance and strategic insights to help high-achieving students stand out in competitive admissions.
              </p>

              <div className="new-hero-actions">
                <Link to="/signup" className="new-btn new-btn-dark new-btn-lg">Get Started</Link>
                <a href="#features" className="new-btn new-btn-ghost new-btn-lg">
                  Learn More
                  <i className="bi bi-arrow-right"></i>
                </a>
              </div>
            </div>

            <div className="new-hero-visual">
              <div className="new-card-stack">
                {highlightCards.map((card, idx) => (
                  <div key={idx} className="new-highlight-card" style={{ marginLeft: `${idx * 20}px` }}>
                    <div className="new-card-icon">
                      <i className={`bi ${card.icon}`}></i>
                    </div>
                    <div className="new-card-content">
                      <div className="new-card-label">{card.label}</div>
                      <div className="new-card-value">{card.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="new-hero-photo">
                <img
                  src="https://images.unsplash.com/photo-1773921403832-aaeba299e510?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=600"
                  alt="Graduate success"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="new-features">
        <div className="new-container">
          <div className="new-section-header">
            <h2 className="new-section-title">Everything you need to stand out</h2>
            <p className="new-section-subtitle">
              Comprehensive tools and insights designed for high-achieving students targeting elite institutions.
            </p>
          </div>

          <div className="new-features-grid">
            {features.map((feature, idx) => (
              <div key={idx} className="new-feature-card">
                <div className="new-feature-icon">
                  <i className={`bi ${feature.icon}`}></i>
                </div>
                <h3 className="new-feature-title">{feature.title}</h3>
                <p className="new-feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="new-how-it-works">
        <div className="new-container">
          <div className="new-section-header">
            <h2 className="new-section-title">A simple, strategic process</h2>
            <p className="new-section-subtitle">From initial assessment to final application strategy</p>
          </div>

          <div className="new-steps-grid">
            {steps.map((step, idx) => (
              <div key={idx} className="new-step-card">
                <div className="new-step-number">{step.number}</div>
                <h3 className="new-step-title">{step.title}</h3>
                <p className="new-step-description">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="new-benefits">
        <div className="new-container">
          <div className="new-benefits-grid">
            <div className="new-benefits-content">
              <h2 className="new-benefits-title">Built for high achievers</h2>
              <p className="new-benefits-subtitle">
                CollegeIQ.ai combines expert college counseling knowledge with advanced AI to give you the competitive edge you need in today's admissions landscape.
              </p>

              <div className="new-benefits-list">
                <div className="new-benefit-item">
                  <h3>Smart college matching</h3>
                  <p>Get a balanced list of reach, target, and safety schools based on real admission data and your unique profile.</p>
                </div>

                <div className="new-benefit-item">
                  <h3>Realistic probabilities</h3>
                  <p>See admission chances for each college using our multi-factor algorithm that considers 15+ variables.</p>
                </div>

                <div className="new-benefit-item">
                  <h3>Strategic guidance</h3>
                  <p>Beyond just numbers—develop a compelling narrative that showcases your intellectual vitality and unique perspective.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="new-cta">
        <div className="new-container">
          <div className="new-cta-content">
            <h2 className="new-cta-title">Start your journey today</h2>
            <p className="new-cta-subtitle">
              Join thousands of high-achieving students who have successfully navigated competitive admissions with CollegeIQ.ai
            </p>

            <form onSubmit={handleSubmit} className="new-cta-form">
              <div className="new-form-group">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  className="new-email-input"
                />
                <button type="submit" disabled={loading} className="new-btn new-btn-white">
                  {loading ? 'Submitting...' : 'Get Started'}
                  <i className="bi bi-arrow-right"></i>
                </button>
              </div>
              {success && <div className="new-success-message">{success}</div>}
              {error && <div className="new-error-message">{error}</div>}
            </form>

            <p className="new-cta-note">No credit card required · Free trial available</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="new-footer">
        <div className="new-container">
          <div className="new-footer-grid">
            <div className="new-footer-brand">
              <span className="new-footer-logo">CollegeIQ.ai</span>
            </div>

            <div className="new-footer-links">
              <h3>Product</h3>
              <ul>
                <li><a href="#features">Features</a></li>
                <li><a href="#">Pricing</a></li>
                <li><a href="#">Success Stories</a></li>
              </ul>
            </div>

            <div className="new-footer-links">
              <h3>Company</h3>
              <ul>
                <li><a href="#">About</a></li>
                <li><a href="#">Contact</a></li>
                <li><a href="#">Careers</a></li>
              </ul>
            </div>

            <div className="new-footer-links">
              <h3>Resources</h3>
              <ul>
                <li><a href="#">Blog</a></li>
                <li><a href="#">Guides</a></li>
                <li><a href="#">Support</a></li>
              </ul>
            </div>
          </div>

          <div className="new-footer-bottom">
            <p>© 2026 CollegeIQ.ai</p>
            <div className="new-footer-legal">
              <a href="#">Privacy</a>
              <a href="#">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
